import * as L from 'wwwroot/lib/leaflet/';
import { AddWhereAmIButton, DroughtCategoryColor, MapCenterDefault, MapDisplayHelpInformation, MapZoomDefault, companyData, formatDroughtCategoryWithColor, initMapLayers, wrzPath } from '../../Common/Helpers/MapHelper';
import { Criteria, getjQuery } from '@serenity-is/corelib';
import { DroughtEventRow, DroughtEventService } from '../../ServerTypes/Drought';
import { DroughtRestrictionCategory } from '../../ServerTypes/Modules';
import { DroughtInfoDialog } from '../../Common/Information/Drought/DroughtMapInfoDialog';
import { marked } from 'wwwroot/lib/marked/lib/marked.esm.js';
import { DroughtMapDisclaimerDialog } from '../../Common/Information/Drought/DroughtMapDisclaimerDialog';
import { CodeFormatter } from '../../Common/Formatters/CamelCaseFormatter';

export class DroughtMapLogic {

    private featureStyle(props) {
        let $ = getjQuery();
        return $.extend({
            weight: 1,
            opacity: 1,
            color: 'DarkSlateGrey',
            dashArray: '4',

        }, props);
    }

    private map: L.Map;
    private infoBox = new L.Control();
    private layerControl = new L.Control.Layers();
    private wrzLayer = L.layerGroup();

    private wrzStatus: { [wrzCode: string]: number } = {};
    private selectedShape;

    constructor() {
    }

    public CreateDroughtMap() {
        this.map = L.map('map').setView(MapCenterDefault, MapZoomDefault);
        this.createInfoBox();
        let $ = getjQuery();
        AddWhereAmIButton(this.map);
        this.createKeyBox();
        MapDisplayHelpInformation(this.map, { position: 'topright' });
        $(".pageInformation").click(() => {
            new DroughtInfoDialog().dialogOpen();
        });

        this.layerControl = initMapLayers(this.map);

        this.layerControl.addOverlay(this.wrzLayer, "Resource Zones");
        this.wrzLayer.addTo(this.map);

        this.drawDroughtEvents();

        var droughtMapDisclaimerCookie = this.getCookieInformation(false);
        if (!droughtMapDisclaimerCookie) {
            new DroughtMapDisclaimerDialog().dialogOpen();
        }

        return;
    }

    private getCookieInformation(intergratedPage: boolean): boolean {
        if (intergratedPage) return true;
        if (document.cookie.length > 0) {
            return (document.cookie.match('(DroughtMapDisclaimer)') != null);
        }
        return false;
    }

    private droughtRows: DroughtEventRow[];
    private wrzFilenameHash = {};
    private wrzUsedForEvents = [];

    private convertToWrzShortName(wrz) {
        return wrz.match(/^[a-zA-Z0-9 ]+/gm)[0];
    }

    private drawDroughtEvents() {
        let $ = getjQuery();
        //create hash table lookup for speed
        companyData.forEach(company => {
            company.wrzList.forEach(wrz => {
                let key = this.convertToWrzShortName(wrz);
                this.wrzFilenameHash[key.toUpperCase()] = wrzPath(company, wrz);
            });
        });

        const todayStart = new Date();
        todayStart.setHours(0, 0, 0, 0);

        const todayEnd = new Date();
        todayEnd.setHours(23, 59, 59, 999);

        const startDateCriteria = Criteria("StartDate").le(todayEnd.toISOString());
        const endDateCriteria = Criteria("EndDate").ge(todayStart.toISOString());
        const endDateNullCriteria = Criteria("EndDate").isNull();
        const fullEndDateCriteria = Criteria.or(endDateCriteria, endDateNullCriteria);
        const fullCriteria = Criteria.and(startDateCriteria, fullEndDateCriteria);

        DroughtEventService.ListDroughtEvents({
            Criteria: fullCriteria,
            IncludeColumns: ['OrganisationName']
        }, response => {
            this.droughtRows = response.Entities;
            let p = response.Entities.map(e => {
                return this.drawDroughtEvent(e);
            });

            $.when(p).then(() => { this.drawAllOtherWrzs(); });
        });
    }

    private drawDroughtEvent(event: DroughtEventRow) {
        const wrz = event.WrzAreaShortNameList;
        this.wrzStatus[wrz] = DroughtRestrictionCategory[event.Category];
        let wrzShortName = wrz.toUpperCase();
        let path = this.wrzFilenameHash[wrzShortName.toUpperCase()];
        //remember which ones have been drawn so we can infill the empty one's later.
        this.wrzUsedForEvents.push(wrzShortName.toUpperCase());
        return this.addWrz(path, this.wrzLayer,
            this.featureStyle({ fillOpacity: 0.3 }),
            event.OrganisationName,
            event);        
    }

    private drawAllOtherWrzs() {
        companyData.filter(x => x.inDroughtMap).forEach(company => {
            company.wrzList.forEach(wrz => {
                let key = this.convertToWrzShortName(wrz);
                //already drawn?
                if (this.wrzUsedForEvents.indexOf(key.toUpperCase()) > -1) return;

                let path = this.wrzFilenameHash[key.toUpperCase()];
                this.addWrz(path, this.wrzLayer,
                    this.featureStyle({ fillOpacity: 0.3 }),
                    company.name, null);
            });
        });
    }

    private createInfoBox() {
        let $ = getjQuery();
        var customControl = L.Control.extend({
            options: { position: 'bottomleft' },
            onAdd: map => {
                var container = L.DomUtil.create('div', 'info hidden');
                $(container).html(`
                        <i class='pull-right fa fa-close' id='close-info-btn'></i>
                        <h3 id='info-title'></h3>

                        <h3 id='info-restriction-type' ></h3>
                        <h3 id='info-date-range' ></h3>
                        <div id='info-detail' class='markdown-output'></div>
                    `);

                return container;
            }
        });
        this.infoBox = new customControl();
        this.map.addControl(this.infoBox);
        $("#close-info-btn").click(() => this.hideinfo());
    }
    
    private highlightFeature = (e) => {
        var layer = e.target;
        //don't wipe the style of the selected zone
        if (this.isSelected(layer)) return;

        var highlightStyle = { weight: 3, color: '#666', dashArray: '', fillOpacity: 0.5 };
        if (layer.highlightColor) {
            highlightStyle['fillColor'] = layer.highlightColor;
            //highlightStyle['fillOpacity'] = 1;
        }
        layer.setStyle(highlightStyle);
    }

    private zoomToFeature(e) {
        this.map.fitBounds(e.target.getBounds());
    }

    private hideinfo() {
        let $ = getjQuery();
        var infoDiv = this.infoBox.getContainer();
        $(infoDiv).addClass('hidden');
    }

    private isSelected(layer: any) {
        if (!this.selectedShape) return false;
        return (layer['_leaflet_id'] == this.selectedShape['_leaflet_id']);
    }

    private addWrz(name, layergroup, style, organisationName, drtRow: DroughtEventRow) {
        let $ = getjQuery();
        return $.getJSON(name, data => {

            let layer = L.geoJSON(data, {
                style: feature => {

                    const regionStatus = this.wrzStatus[feature.properties.WRZAREASHO];

                    // Apply color, if no matching color, fallback to green/Normal Conditions
                    if (regionStatus && DroughtCategoryColor[regionStatus]) {
                        style.fillColor = DroughtCategoryColor[regionStatus];
                    } else {
                        style.fillColor = DroughtCategoryColor["NormalConditions"];
                    }

                    return style;
                },
                onEachFeature: (feature, layer) => {
                    let tooltip = `${organisationName}<br/> ${feature.properties.WRZAREANAM}<br/> `;
                    if (drtRow) {
                        tooltip += `${this.getFormatCategory(drtRow)} <br/> ${this.getFormatDateRange(drtRow)}`;
                    } else {
                        tooltip += 'Normal Conditions';
                    }

                    layer.bindTooltip(tooltip,
                        { permanent: false, opacity: 0.7, sticky: true });//.openTooltip();
                    layer.on({
                        mouseover: this.highlightFeature,
                        mouseout: (e) => {
                            var layer = e.target;
                            //don't wipe the style of the selected zone
                            if (!this.isSelected(layer)) {
                                layer.setStyle(style);
                            }

                        },
                        click: (e) => {
                            // enable to get a lat long ref for every click
                            //console.log(this.fmt4dp(e.latlng.lat) + "," + this.fmt4dp(e.latlng.lng));
                            //  this.zoomToFeature(e);
                            this.displayInfoBox(feature.properties, organisationName, drtRow);

                            if (this.selectedShape) {
                                //reset old zone
                                this.selectedShape.setStyle(this.featureStyle({ fillOpacity: 0.3 }));
                            }

                            this.selectedShape = e.target;
                            this.selectedShape.setStyle(
                                //{ weight: 4, color: 'white', fillOpacity: 0.9 }
                                { fillOpacity: 0.9 }
                            );
                        }
                    });
                }
            });
            layergroup.addLayer(layer);
        });
    }

    private displayInfoBox(wrzProps: any, organisationName: string, row: DroughtEventRow) {
        let $ = getjQuery();
        var infoDiv = this.infoBox.getContainer();
        $(infoDiv).removeClass('hidden');

        let wrzAreaName = wrzProps.WRZAREANAM;
        $("#info-title").text(`${organisationName}, ${wrzAreaName}`);

        if (!row) {
            $("#info-restriction-type").text('Normal Conditions');
            $("#info-date-range").empty();
            $("#info-detail").empty();
        } else {
            $("#info-restriction-type").text(this.getFormatCategory(row));
            $("#info-date-range").text(this.getFormatDateRange(row));
            $("#info-detail").html(row.Detail ? marked.parse(row.Detail) : '');
        }
    }

    private getFormatDateRange(row: DroughtEventRow) {
        if (!row.StartDate) return '';
        let startDate = row.StartDate ? this.dateMonthYear(row.StartDate) : '';
        // let endDate = row.EndDate ? ' to ' + this.dateMonthYear(row.EndDate) : '';
        return 'From ' + startDate;
    }

    private dateMonthYear(date: string) {
        return (new Date(date)).toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' });
    }

    private getFormatCategory(row: DroughtEventRow) {
        return CodeFormatter(DroughtRestrictionCategory[row.Category]);
    }

    private createKeyBox() {
        let $ = getjQuery();
        var customControl = L.Control.extend({
            options: { position: 'bottomright' },
            onAdd: map => {

                var container = L.DomUtil.create('div', 'info key');
                $(container).html(
                    formatDroughtCategoryWithColor(DroughtRestrictionCategory.NormalConditions) + "<br/>" +
                    formatDroughtCategoryWithColor(DroughtRestrictionCategory.DemandAdvisoryNotice) + "<br/>" +
                    formatDroughtCategoryWithColor(DroughtRestrictionCategory.DryWeatherConditions) + "<br/>" +
                    formatDroughtCategoryWithColor(DroughtRestrictionCategory.ProlongedDryWeather) + "<br/>" +
                    formatDroughtCategoryWithColor(DroughtRestrictionCategory.SevereDrought)
                );

                return container;
            }
        });

        this.map.addControl(new customControl());
    }
}
